import { webSocketUrl } from '../config';

export type ServerStatus =
  | { state: 'unconfigured' }
  | { state: 'checking' }
  | { state: 'waking'; attempt: number }
  | { state: 'online'; version: string; latencyMs: number }
  | { state: 'offline'; detail?: string };

interface HealthBody {
  status: string;
  version: string;
}

const HEALTH_TIMEOUT_MS = 12_000;
const WS_TIMEOUT_MS = 8_000;
/** A page still booting the scene can stall long enough to miss its own timer; try again. */
const WS_ATTEMPTS = 3;

/**
 * Checks `/health` (with retries: a scaled-to-zero Container App needs a few seconds to wake up),
 * then measures a WebSocket round trip.
 */
export async function checkServer(
  serverUrl: string,
  onProgress: (status: ServerStatus) => void,
  attempts = 4,
): Promise<ServerStatus> {
  if (!serverUrl) return { state: 'unconfigured' };

  let health: HealthBody | undefined;
  let lastError = '';
  for (let attempt = 1; attempt <= attempts && !health; attempt++) {
    onProgress(attempt === 1 ? { state: 'checking' } : { state: 'waking', attempt });
    try {
      health = await fetchHealth(serverUrl);
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);
      await sleep(1500 * attempt);
    }
  }
  if (!health) return { state: 'offline', detail: lastError };

  for (let attempt = 1; attempt <= WS_ATTEMPTS; attempt++) {
    try {
      const latencyMs = await measureRoundTrip(webSocketUrl(serverUrl));
      return { state: 'online', version: health.version, latencyMs };
    } catch (err) {
      lastError = `websocket: ${err instanceof Error ? err.message : String(err)}`;
      await sleep(1000 * attempt);
    }
  }
  return { state: 'offline', detail: lastError };
}

async function fetchHealth(serverUrl: string): Promise<HealthBody> {
  const response = await fetch(`${serverUrl}/health`, {
    signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const body = (await response.json()) as HealthBody;
  if (body.status !== 'ok') throw new Error(`status ${body.status}`);
  return body;
}

function measureRoundTrip(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    const timer = setTimeout(() => {
      ws.close();
      reject(new Error('timeout'));
    }, WS_TIMEOUT_MS);
    const finish = (fn: () => void) => {
      clearTimeout(timer);
      ws.close();
      fn();
    };
    ws.onerror = () => finish(() => reject(new Error('connection failed')));
    ws.onmessage = (event) => {
      const msg = JSON.parse(String(event.data)) as { t: string; sent?: number };
      if (msg.t === 'hello') ws.send(JSON.stringify({ t: 'ping', sent: performance.now() }));
      else if (msg.t === 'pong' && typeof msg.sent === 'number')
        finish(() => resolve(Math.round(performance.now() - msg.sent!)));
    };
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
